import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { getWarehouseType, WarehouseTypeIcon } from '@dbt-labs/dbt-dag';
import {
  Link,
  MenuItem,
  RyeconLineage,
  RyeconQuery,
  RyeconShare,
  toast,
} from '@dbt-labs/sourdough';

import { DataPlatform, WarehouseEntityType } from '@/__generated__/graphql';
import {
  ActionButtons,
  OpenInMenuGroup,
  OpenInMenuItem,
} from '@/components/ActionButtons';
import { PageHeading } from '@/components/PageHeading';
import {
  RyeconFavoriteFilled,
  RyeconFavoriteOutline,
} from '@/context/AccountNavigation/RyeconFavorite';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import {
  useAddFavoriteAsset,
  useDeleteFavoriteAsset,
  useFavoriteAssets,
} from '@/hooks/admin/favoriteAssets';
import {
  getWarehouseTypeFromPlatform,
  useInsightsDWHHandoff,
} from '@/hooks/query/handoffs';
import { useCanvasDWHHandoff } from '@/hooks/visual-editor/handoffs';
import { HandoffDWHObjectTypes } from '@/hooks/visual-editor/types';
import { CanvasHandoffModal } from '@/pages/account/project/resource/details/components/CanvasHandoffModal';
import {
  HeaderIconItem,
  TextIconPair,
} from '@/pages/account/project/resource/details/components/Header';
import { ButtonAction } from '@/pages/account/project/resource/details/components/HeaderButtons';
import InsightsHandoffModal from '@/pages/account/project/resource/details/components/InsightsHandoffModal';
import { AssetBreadcrumbs } from './AssetBreadcrumbs';

type AssetDetailsHeaderProps = PropsWithChildren<{}> & {
  urn: string | undefined;
  qualifiedName: string;
  assetType: WarehouseEntityType;
  platform: DataPlatform | null | undefined;
  headerIcons: TextIconPair[];
  willHaveHeaderIcons?: boolean;
  title: string;
};

export const AssetDetailsHeader: FC<AssetDetailsHeaderProps> = ({
  headerIcons,
  willHaveHeaderIcons = true,
  title,
  platform,
  urn,
  qualifiedName,
  assetType,
  children,
}) => {
  const { showInsightsLink } = useInsightsDWHHandoff(
    assetType as HandoffDWHObjectTypes,
    urn ?? '',
    platform,
  );
  const { showCanvasCreateLink } = useCanvasDWHHandoff(
    assetType as HandoffDWHObjectTypes,
    urn ?? '',
    getWarehouseTypeFromPlatform(platform),
  );

  const { accountId } = useDbtCloudAccount();

  const { data: favoriteAssets } = useFavoriteAssets(accountId);

  const isFavorite = favoriteAssets?.some((asset) => asset.asset_id === urn);

  const { mutate: addFavoriteAsset } = useAddFavoriteAsset();
  const { mutate: removeFavoriteAsset } = useDeleteFavoriteAsset();

  const moreItems = useMemo(() => {
    const actions: MenuItem[] = [];
    actions.push({
      label: isFavorite ? 'Remove from favorites' : 'Add to favorites',
      value: 'favorite',
      ryecon: isFavorite ? RyeconFavoriteFilled : RyeconFavoriteOutline,
    });

    // this a fixed action that is always shown
    actions.push({
      label: 'Share',
      value: 'share',
      ryecon: RyeconShare,
    });

    return actions;
  }, [isFavorite]);

  const [isCanvasModalOpen, setIsCanvasModalOpen] = useState(false);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);

  const contextMenuOnClick = useCallback(
    (value: ButtonAction) => {
      switch (value) {
        case 'query':
          setIsInsightsModalOpen(true);
          break;
        case 'canvas-create':
          setIsCanvasModalOpen(true);
          break;
        case 'canvas-edit':
          setIsCanvasModalOpen(true);
          break;
        case 'share':
          navigator.clipboard.writeText(window.location.href);
          toast.success('Copied link to clipboard', { id: 'copy-link' });
          break;
        case 'favorite':
          if (isFavorite) {
            removeFavoriteAsset({
              accountId: accountId ?? 0,
              assetId: urn,
            });
          } else {
            addFavoriteAsset({
              accountId: accountId ?? 0,
              assetId: urn,
              name: title,
              platform: platform ?? 'dbt',
              assetType: assetType,
            });
          }
          break;
        default:
          break;
      }
    },
    [
      accountId,
      addFavoriteAsset,
      assetType,
      isFavorite,
      platform,
      removeFavoriteAsset,
      title,
      urn,
    ],
  );

  const openInItems: (OpenInMenuItem | OpenInMenuGroup)[] = useMemo(
    () => [
      {
        label: 'Insights',
        ryecon: RyeconQuery,
        show: showInsightsLink,
        items: [
          {
            label: 'Analyze data',
            value: 'query',
          },
        ],
      },
      {
        label: 'Canvas',
        ryecon: RyeconLineage,
        includeTopDivider: true,
        show: showCanvasCreateLink,
        items: [
          {
            label: 'Create downstream model',
            value: 'canvas-create',
            description: 'Use this table to build a model',
          },
        ],
      },
    ],
    [showCanvasCreateLink, showInsightsLink],
  );

  const asset =
    urn && assetType
      ? {
          id: urn,
          qualifiedName,
          type: assetType as HandoffDWHObjectTypes,
          warehouseType: getWarehouseTypeFromPlatform(platform),
        }
      : null;

  return (
    <>
      {asset && (
        <>
          <CanvasHandoffModal
            asset={asset}
            isOpen={isCanvasModalOpen}
            setIsOpen={setIsCanvasModalOpen}
          />
          <InsightsHandoffModal
            asset={asset}
            isOpen={isInsightsModalOpen}
            setIsOpen={setIsInsightsModalOpen}
          />
        </>
      )}
      <AssetBreadcrumbs />
      <div className="mb-2 mt-4 flex">
        <span className="h-10 min-w-0 flex-1">
          {children ? (
            children
          ) : (
            <div className="flex items-center overflow-hidden truncate whitespace-nowrap">
              <WarehouseTypeIcon
                warehouse={getWarehouseType(platform ?? '')}
                size="xl"
                className="mr-2 min-w-5"
              />
              <PageHeading className="w-min truncate">{title}</PageHeading>
            </div>
          )}
        </span>
        <span className="flex-0 mx-2 flex items-center gap-3">
          <ActionButtons
            openInItems={openInItems}
            onClick={contextMenuOnClick}
            moreItems={moreItems}
          />
        </span>
      </div>
      <div
        className={twJoin(
          'mb-4 flex gap-4 duration-300 motion-reduce:duration-0',
          headerIcons.length ? 'opacity-100' : 'opacity-0',
          willHaveHeaderIcons && 'min-h-[26px]',
        )}
      >
        {headerIcons.map(({ key, ...props }: TextIconPair) => {
          if (props.href) {
            return (
              <Link key={props.text ?? key} isInternal={false} to={props.href}>
                <HeaderIconItem {...props} />
              </Link>
            );
          }
          return <HeaderIconItem key={props.text ?? key} {...props} />;
        })}
      </div>
    </>
  );
};
